import { useState, useEffect, useMemo } from "react";
import { GitBranch, Globe, Maximize2 } from "lucide-react";
import DownwardTreeView from "./DownwardTreeView";
import UpwardTreeView from "./UpwardTreeView";
import { ConsumerNetworkView } from "./ConsumerNetworkView";
import { DependencyViewControls } from "./DependencyViewControls";
import { Notification } from "./Notification";
import { UpstreamNetworkView } from "./UpstreamNetworkView";
import heartLoading from "../../../assests/heartLoading.gif";
import axios from "axios";
import { Tooltip } from "@mui/material";
import PowerBIIcon from "../../../assests/power-bi-icon.svg";

export const DataContractDetails = ({ contract, prefetchedConsumerData, initialViewMode = "network", initialRenderType = "downstream" }) => {
  const [viewMode, setViewMode] = useState(initialViewMode);
  const [renderType, setRenderType] = useState(initialRenderType);
  // Check if current user is the product owner or an admin
  const userId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("role");
  const isProductOwner = contract.productOwner === userId;
  const isAdmin = userRole === "Admin";
  const canSendNotification = isProductOwner || isAdmin;
  // If prefetchedConsumerData is passed from parent, use it directly — no fetch, no loader
  const [consumerData, setConsumerData] = useState(prefetchedConsumerData);
  const [loading, setLoading] = useState(!prefetchedConsumerData);

  // Fetch cosnsumer dependencies and email list
  useEffect(() => {
    // If data already passed from parent, skip fetch entirely
    if (prefetchedConsumerData) return;

    const fetchConsumerData = async () => {
      if (!contract?.id) {
        setLoading(false);
        return;
      };

      if (!loading) {
        setLoading(true);
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/get-consumer-dependencies/${contract?.id}`,
        );
        setConsumerData(response.data);
      } catch (err) {
        console.error("Failed to fetch consumer data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConsumerData();
  }, [contract?.id]);

  // Collect all unique product IDs from both upstream and downstream dependencies
  const graphProductIds = useMemo(() => {
    const ids = new Set([contract?.id]); // Add root contract ID

    const collectProductIds = (node) => {
      if (!node) return;
      ids.add(node.id);
      if (node.dependencies && Array.isArray(node.dependencies)) {
        node.dependencies.forEach(dep => collectProductIds(dep));
      }
    };

    // Add all upstream dependencies
    if (consumerData?.upstreamDependencies) {
      consumerData.upstreamDependencies.forEach(dep => collectProductIds(dep));
    }

    // Add all downstream dependencies
    if (consumerData?.consumerDependencies) {
      consumerData.consumerDependencies.forEach(dep => collectProductIds(dep));
    }

    return ids;
  }, [contract?.id, consumerData?.upstreamDependencies, consumerData?.consumerDependencies]);

  return (
    <>
      {loading ? (
        <div className="relative bg-white flex items-center justify-center z-50 min-h-[80vh]">
          <div className="flex flex-col items-center space-y-3">
            <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
            <span className="text-gray-700 text-[20px] text-center">
              Loading consumer dependencies...
            </span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-between-items item-start px-4 py-3 gap-6">
            <p className="text-gray-600 flex-1">{contract.description}</p>
            <div className="bg-white border border-gray-300 rounded-lg p-2 shadow-sm">
              <div className="text-xs font-medium text-gray-700 mb-2">
                Legend
              </div>
              <div className="space-y-1">
                <div className="flex items-center text-xs">
                  <span className="text-blue-600">Data Contracts</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-700">
                  <img src={PowerBIIcon} alt="Power BI" className="w-4 h-4 object-contain" />
                  <span>Power BI report</span>
                </div>
                <div className="flex items-center gap-2 text-xs leading-none  cursor-pointer">
                  <Tooltip
                    title="All the rules of the dependent parent asset have passed."
                    PopperProps={{
                      sx: { zIndex: 999999999 },
                    }}
                  >
                    <div className="flex items-center gap-1  cursor-pointer ">
                      <Globe
                        className="w-8 h-8 text-white block stroke-green-500"
                        title="All the rules of the dependent parent asset have passed."
                      />
                    </div>
                  </Tooltip>

                  <Tooltip
                    title="One or more rules of the dependent parent asset has failed"
                    PopperProps={{
                      sx: { zIndex: 999999999 },
                    }}
                  >
                    <div className="flex items-center gap-1  cursor-pointer">
                      <Globe className="w-8 h-8 text-white block stroke-[#FFBF00]" />
                    </div>
                  </Tooltip>
                  <Tooltip
                    title="One or more dimensions of the dependent parent asset has failed."
                    PopperProps={{
                      sx: { zIndex: 999999999 },
                    }}
                  >
                    <div className="flex items-center gap-1  cursor-pointer">
                      <Globe className="w-8 h-8 text-white block stroke-red-500" />
                    </div>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
          <div className="p-3 space-y-4">
            <div className="space-y-6">
              <div className="bg-[#F2F9F7] rounded-xl p-3">
                <div className="mb-4">
                  <div className="mb-3">
                    <h3 className="text-[16px] font-semibold text-gray-900 flex items-center gap-2">
                      <GitBranch className=" h-5 text-blue-600" />
                      Consumer Dependencies
                    </h3>
                    <h4>
                      The color of each product represents its overall health —
                      based on the combined health of its parent assets if
                      subscribed, or its own asset health if not subscribed
                    </h4>
                  </div>
                  <div className="flex flex-wrap items-center gap-4">
                    <DependencyViewControls
                      viewMode={viewMode}
                      onViewModeChange={setViewMode}
                      renderType={renderType}
                      onRenderTypeChange={setRenderType}
                    />
                    <a
                      href={`/traffic-light-fullscreen?contractId=${contract.id}&viewMode=${viewMode}&renderType=${renderType}`}
                      target="_self"
                      className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold whitespace-nowrap border border-blue-200 bg-blue-50 text-blue-700 shadow-sm transition-colors hover:border-blue-300 hover:bg-blue-100 ml-auto"
                      title="Open in full screen (right-click to open in new tab)"
                    >
                      <Maximize2 className="w-4 h-4" />
                      Full Screen
                    </a>
                  </div>
                </div>
                <div className="transition-all duration-300">
                  {viewMode === "tree" ? (
                    <>
                      {renderType === "upstream" ? (
                        <UpwardTreeView
                          contractName={contract.name}
                          contractStatus={consumerData?.subscription_status || contract.status}
                          contractProductId={contract.id}
                          contractSubscribedAssets={consumerData?.subscribedAssets || []}
                          graphProductIds={graphProductIds}
                          dependencies={consumerData?.upstreamDependencies}
                        />
                      ) : (
                        <DownwardTreeView
                          contractName={contract.name}
                          contractStatus={contract.status}
                          contractProductId={contract.id}
                          contractSubscribedAssets={consumerData?.subscribedAssets || []}
                          graphProductIds={graphProductIds}
                          dependencies={consumerData?.consumerDependencies}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {renderType === "upstream" ? (
                        <UpstreamNetworkView
                          consumers={consumerData?.upstreamDependencies || []}
                          contractName={contract.name}
                          contractStatus={consumerData?.subscription_status || contract.status}
                          contractProductId={contract.id}
                          contractSubscribedAssets={consumerData?.subscribedAssets || []}
                          graphProductIds={graphProductIds}
                        />
                      ) : (
                        <ConsumerNetworkView
                          consumers={consumerData?.consumerDependencies || []}
                          contractName={contract.name}
                          contractStatus={contract.status}
                          contractProductId={contract.id}
                          contractSubscribedAssets={consumerData?.subscribedAssets || []}
                          graphProductIds={graphProductIds}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6"></div>
            </div>
          </div>
          {canSendNotification && (
            <div className="p-3">
              <Notification
                productId={contract?.id}
                emailList={consumerData?.emails || []}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
